import { useMemo } from 'react';
import useStyles from './useStyles';
import { Grid, IconButton } from '@material-ui/core';
import { UploadedDrawingFileType } from '@modules/request';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import { TrashCan } from '@carbon/icons-react';
import {
  ServiceTypes,
  OptionComponent,
  SelectFileDialogProps
} from './RequestForQuotationUpload';
import { Enum_Action_Button_Enum } from '@hooks/useUserActionLog';
import { useFormContext as useFormContextFromContainer } from 'react-hook-form';
import { CapaQuotationServiceType } from '@generated/graphql';

interface Props extends ServiceTypes {
  uploadedDrawingFiles: UploadedDrawingFileType[];
  onSelectFile: (payload: SelectFileDialogProps) => void;
}

const RequestForQuotationFileCard = ({
  uploadedDrawingFiles,
  detailedServiceType,
  serviceType,
  onSelectFile
}: Props) => {
  const classes = useStyles();
  const { errors } = useFormContextFromContainer();

  const logId = useMemo(() => {
    if (serviceType === CapaQuotationServiceType.Cnc) {
      return Enum_Action_Button_Enum.Button_RFQ_CNC_Select_Options;
    }

    if (serviceType === CapaQuotationServiceType.Printing) {
      return Enum_Action_Button_Enum.Button_RFQ_PRINTING_Select_Options;
    }

    if (serviceType === CapaQuotationServiceType.Inj) {
      return Enum_Action_Button_Enum.Button_RFQ_INJ_Select_Options;
    }

    if (serviceType === CapaQuotationServiceType.SheetMetal) {
      return Enum_Action_Button_Enum.Button_RFQ_SHEET_METAL_Select_Options;
    }

    if (serviceType === CapaQuotationServiceType.Casting) {
      return Enum_Action_Button_Enum.Button_RFQ_CASTING_Select_Options;
    }
  }, [serviceType]);

  return (
    <Grid container className={classes['rfq-drawingfiles-card']}>
      {uploadedDrawingFiles.map((file, index) => {
        if (!file) {
          return;
        }

        const isError = Object.keys(errors).some((key) => key === file.fileId);

        const fileName = () => {
          if (file?.fileName) {
            const fileName =
              file.fileName && file.fileName.length > 15
                ? file.fileName.slice(0, 15) + '...'
                : file.fileName;

            const extension =
              file.fileName &&
              file.fileName
                .slice(file.fileName.lastIndexOf('.') + 1)
                .toLowerCase();

            return fileName + extension;
          }

          return '';
        };

        return (
          <Grid className="card" item xs={12} key={index}>
            <div className="card-header">
              <CustomizedTypography
                variant="body2"
                color="textPrimary"
                className="index"
              >
                {index + 1}
              </CustomizedTypography>

              <CustomizedTypography
                className="fileName"
                variant="body2"
                color="textPrimary"
                fontWeight="bold"
              >
                {fileName()}
              </CustomizedTypography>

              <IconButton
                size="small"
                className="delete-button"
                onClick={() => {
                  onSelectFile({ fileId: file.fileId, mode: 'delete' });
                }}
              >
                <TrashCan size={24} />
              </IconButton>
            </div>
            <div className="card-body">
              <div className="card-data">
                <CustomizedTypography
                  className="data-title"
                  variant="body2"
                  color="textPrimary"
                  fontWeight="bold"
                >
                  재료*
                </CustomizedTypography>

                <div className="data-options">
                  {file.isValid ? (
                    <CustomizedTypography variant="body2" color="textPrimary">
                      <OptionComponent
                        {...file}
                        serviceType={serviceType}
                        detailedServiceType={detailedServiceType}
                      />
                    </CustomizedTypography>
                  ) : (
                    <CustomizedTypography
                      variant="body2"
                      color={isError ? 'error' : 'textSecondary'}
                    >
                      선택한 재료 옵션이 없습니다.
                    </CustomizedTypography>
                  )}
                </div>
              </div>

              <CustomizedButton
                logId={logId}
                fullWidth
                variant={file.isValid ? 'outlined' : 'contained'}
                onClick={() => {
                  onSelectFile({ fileId: file.fileId, mode: 'change' });
                }}
              >
                {file.isValid ? '수량 및 재료 수정' : '수량 및 재료 입력'}
              </CustomizedButton>
            </div>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default RequestForQuotationFileCard;
